import {
  ColDef,
  GridApi,
  GridState,
  IRowNode,
  ISetFilterParams,
  KeyCreatorParams,
  StateUpdatedEvent,
  TooltipCallbackFunc,
  ValueFormatterParams,
} from 'ag-grid-community';
import { get, merge, omit } from 'lodash-es';
import { FieldCellEditor } from '../components/AgTable/cell-editors/FieldCellEditor';
import { RendererType } from '../data-models/field-definition';
import { isNumericRenderer } from '../data-models/field2.data-model';
import { field3ToColumnDef, field3ToFormField, IField3 } from '../data-models/field3.data-model';
import { numericCellClasses } from '../pages/Finance2/common-grid-defs/commonColDefs';
import { editableCellClass } from '../theme/gridStyle';
import { createFormField } from '../view-models/form.view-model';
import { AgFormatterService, rawValueText } from './ag-formatter-service';
import { genericComparator } from './comparators';
import { isDateOrDateString, plainDateFromDateOrDateString } from './date-utilts';
import { ExcelFormatterIdPrefix } from './excel-formatter-service';
import { DateFormattersId } from '@foresightdata/web-common-core';
import { FormatterService } from '@foresightdata/web-common-formatter-service';
import { IDisplayField } from '../view-models/display-field.view-model';
import { ParserService, StandardParserId } from './parser-service';

export function withOverrides<T>(defs: ColDef<T>[], overrides: Record<string, ColDef>) {
  const defMap = new Map<string, ColDef<T>>(defs.map((def) => [(def.field ?? def.colId) as string, def]));

  for (const [field, customDefs] of Object.entries(overrides)) {
    if (defMap.has(field)) {
      defMap.set(field, merge({}, defMap.get(field), customDefs));
    }
  }
  return Array.from(defMap.values());
}

export function agDateColumnFilterComparator(
  filterLocalDateAtMidnight: Date,
  cellValue: string | null | undefined
) {
  if (cellValue == null) {
    return 1;
  }
  const filteredIsoDate = plainDateFromDateOrDateString(filterLocalDateAtMidnight);
  const timelessCellValue = cellValue.slice(0, 10);
  return timelessCellValue === filteredIsoDate ? 0 : timelessCellValue > filteredIsoDate ? 1 : -1;
}

const isoDateColumnDefBase = () => {
  return {
    cellClass: ['monospace', 'text-align-right', `${ExcelFormatterIdPrefix}${DateFormattersId.date}`],
    comparator: genericComparator,
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: agDateColumnFilterComparator,
      includeTime: false,
    },
  };
};

export function getIsoDateColDef<T>(overrides: Partial<ColDef> = {}): ColDef<T> {
  return merge({}, isoDateColumnDefBase(), overrides) as ColDef<T>;
}

export function agDateTimeColumnFilterComparator(
  filterLocalDateAtMidnight: Date,
  cellValue: string | null | undefined
) {
  if (cellValue == null) {
    return 1;
  }
  const filteredIsoDate = filterLocalDateAtMidnight.toISOString().slice(0, 19); // exclude milliseconds
  const cellValueWithNoMillis = cellValue.slice(0, 19);
  return cellValueWithNoMillis === filteredIsoDate ? 0 : cellValueWithNoMillis > filteredIsoDate ? 1 : -1;
}

const isoDateTimeColumnDefBase = () => {
  return {
    ...isoDateColumnDefBase(),
    cellClass: ['monospace', 'text-align-right', `${ExcelFormatterIdPrefix}${DateFormattersId.dateTime}`],
    filterParams: {
      comparator: agDateTimeColumnFilterComparator,
      includeTime: true, // supported from v34
    },
  };
};

export function getIsoDateTimeColDef<T>(overrides: Partial<ColDef> = {}): ColDef<T> {
  return merge({}, isoDateTimeColumnDefBase(), overrides) as ColDef<T>;
}

export function getEditableDateFieldColDefs(key: string): ColDef {
  return {
    cellClass: `${editableCellClass} ${numericCellClasses.join(' ')}`,
    cellEditor: FieldCellEditor,
    cellEditorParams: {
      formField: createFormField({
        autoFocus: true,
        key,
        renderer: RendererType.date,
        fullWidth: true,
      }),
    },
    cellDataType: false,
    editable: true,
    field: key,
    valueFormatter: (params: ValueFormatterParams) => {
      const formatter = AgFormatterService.get().getFormatterForId(DateFormattersId.dateLocal);
      if (params.value && isDateOrDateString(params.value, RendererType.date)) {
        return formatter(params);
      }
      return params.value;
    },
  };
}
export function getEditableFieldColDefs(field: IField3<unknown>, key: string): ColDef {
  const formField = field3ToFormField(field);
  const commonCellClasses = field3ToColumnDef(field).cellClass;
  return {
    cellClass: `${editableCellClass} ${Array.isArray(commonCellClasses) ? commonCellClasses.join(' ') : ''}`,
    cellEditor: FieldCellEditor,
    cellEditorParams: {
      formField: {
        ...formField,
        autoFocus: true,
        fullWidth: true,
        key,
      },
    },
    editable: true,
    field: key,
    valueParser: (params) => {
      const rendererType = formField.renderer;
      if (rendererType && isNumericRenderer(rendererType)) {
        return ParserService.get().parse(StandardParserId.Number, params.newValue);
      }
      return params.newValue;
    },
    cellEditorPopup: formField.renderer === RendererType.multiSelect,
  };
}

/**
 * A multiSelect cell holds an array. The Set Filter lists and matches each element of it on its own,
 * and by default keys, labels and sorts each element through the column's formatter, which formats a
 * whole array and throws on a single value. These params key each element as itself and label and sort
 * it with the field's single-select formatter. `undefined` for any other renderer.
 */
export function multiSelectSetFilterParams(field: IDisplayField<unknown>): ISetFilterParams | undefined {
  if (field.renderer !== RendererType.multiSelect) {
    return undefined;
  }
  // Without its own formatter, which formats the whole array, the field formats through its options.
  const elementField: IDisplayField<unknown> = {
    ...field,
    renderer: RendererType.singleSelect,
    formatter: undefined,
  };
  const formatLabel = FormatterService.get().getFormatterForFormField<unknown>(elementField);
  // The sort runs inside the Set Filter, where a throw breaks the list, so it falls back like the
  // grid formatter does.
  const labelFor = (value: unknown) => {
    try {
      return String(formatLabel(value));
    } catch {
      return rawValueText(value);
    }
  };

  return {
    keyCreator: (params: KeyCreatorParams) => rawValueText(params.value),
    valueFormatter: AgFormatterService.get().getFormatterForFormField(elementField),
    comparator: (a: unknown, b: unknown) => labelFor(a).localeCompare(labelFor(b)),
  };
}

export const DefaultIgnoreList = new Set<StateUpdatedEvent['sources'][0]>([
  'cellSelection',
  // AG Grid 36 fires it after a grouped grid loads, with no user edit; we have no header renaming
  'columnHeaderName',
  'focusedCell',
  'scroll',
]);

export function getStateToPersist(gridState: GridState, ignoreSources: Set<string>) {
  return omit(gridState, Array.from(ignoreSources));
}

export function doOnGridStateUpdated(
  cb: (gridState: GridState) => Promise<void> | void,
  ignoreSources: Set<string>
) {
  return function (event: StateUpdatedEvent) {
    const skippedForInit = event.sources?.includes('gridInitializing');
    const skippedForAllIgnored = (event.sources ?? []).every((s) => ignoreSources.has(s));
    if (skippedForInit || skippedForAllIgnored) {
      return;
    }
    cb(getStateToPersist(event.state, ignoreSources));
  };
}

export function highlightRow({ api, node }: { api: GridApi; node: IRowNode }) {
  function flashRow() {
    api.flashCells({ rowNodes: [node], flashDuration: 1500 });
    api.removeEventListener('bodyScrollEnd', flashRow);
  }

  api.addEventListener('bodyScrollEnd', flashRow);
  api.ensureNodeVisible((row: IRowNode) => row.id === node.id);
}

/**
 * A `tooltip` callback that shows a row field: the AG Grid 36.2 replacement for the deprecated `tooltipField`.
 * Like `tooltipField`, a group row with no data, or one aggregating this column, shows the cell's own value.
 */
export function fieldTooltip<TData>(field: string): TooltipCallbackFunc<TData> {
  return ({ data, node, column, value }) => {
    const colId = column && 'getColId' in column ? column.getColId() : undefined;
    const aggregated = !!node?.group && colId !== undefined && node.aggData?.[colId] !== undefined;
    if (data == null || aggregated) {
      return node?.group ? value : undefined;
    }
    return get(data, field);
  };
}
